/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useMemo } from 'react';
import { Shield, X, ChevronDown, Check, PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { roleService } from '../services/roleService';
import toastService from '../utils/toastService';
import PermissionGrid from '../components/PermissionGrid';
import { useAuth } from '../context/AuthContext';

const NewRole = () => {
  const navigate = useNavigate();
  const { userScope } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentUserScope = userScope || 'platform';
  const [form, setForm] = useState({
    name: '',
    displayName: '',
    scope: currentUserScope,
    isDefault: false,
    permissionIds: []
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [isScopeOpen, setIsScopeOpen] = useState(false);
  const scopeDropdownRef = React.useRef(null);

  const availableScopes = useMemo(() => {
    if (currentUserScope === 'platform') { return ['platform', 'account', 'sub_account']; }
    if (currentUserScope === 'account') { return ['account', 'sub_account']; }
    return ['sub_account'];
  }, [currentUserScope]);

  const filteredPermissions = useMemo(() => {
    const excludedKeys = [];

    if (form.scope === 'account') {
      excludedKeys.push('accounts.create', 'accounts.update', 'accounts.delete');
    } else if (form.scope === 'sub_account') {
      excludedKeys.push(
        'accounts.create', 'accounts.update', 'accounts.delete',
        'sub_accounts.create', 'sub_accounts.update', 'sub_accounts.delete'
      );
    }else{
      // If scope is platform, we don't exclude any permissions since platform includes all scopes
    }

    return permissions.filter(p => !excludedKeys.includes(p.key));
  }, [permissions, form.scope]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);

        const res = await roleService.getPermissions();
        const permData = res.data?.data || res.data || [];

        setPermissions(permData);
      } catch (err) {
        toastService.error('Failed to load permissions');
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        scopeDropdownRef.current &&
        !scopeDropdownRef.current.contains(event.target)
      ) {
        setIsScopeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!form.name) { errors.name = 'Role name is required'; }
    if (!form.displayName)
      errors.displayName = 'Display name is required';
    if (form.permissionIds.length === 0)
      errors.permissionIds = 'Select at least one permission';

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      const fieldLabels = {
        name: 'Role name',
        displayName: 'Display name',
        permissionIds: 'Permissions'
      };

      setFieldErrors(prev => ({
        ...prev,
        [field]: `${fieldLabels[field] || field} is required`
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) { return; }

    try {
      setSubmitting(true);

      await roleService.createRole(form);

      toastService.success('Role created successfully');

      navigate('/roles');
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || 'Failed to create role');
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>

        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">
          Synchronizing Role Data...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md">

        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title flex items-center gap-3">
              Create Role
            </h1>
          </div>

          <button
            onClick={() => navigate('/roles')}
            className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form Area */}
        <div className="p-6 sm:p-8 max-w-[1200px]">
          <form onSubmit={handleSubmit} className="space-y-10">

            {/* Identity & Scope */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                {/* Name */}
                <div className="space-y-2">
                  <label htmlFor="name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Name *
                  </label>

                  <input
                    id="name"
                    type="text"
                    maxLength={50}
                    placeholder="e.g. CUSTOM_ROLE"
                    className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content placeholder:text-base-content/20 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all font-bold uppercase ${fieldErrors.name
                      ? 'border-error'
                      : 'border-base-300'
                      }`}
                    value={form.name}
                    onBlur={(e) =>
                      handleBlur('name', e.target.value)
                    }
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase().replace(/\s+/g, '_');
                      setForm(f => ({ ...f, name: val }));
                      if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: null }));
                    }}
                  />

                  {fieldErrors.name && (
                    <p className="text-error text-xs mt-1 font-medium">
                      {fieldErrors.name}
                    </p>
                  )}
                </div>

                {/* Display Name */}
                <div className="space-y-2">
                  <label htmlFor="displayName" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Display Name *
                  </label>

                  <input
                    id="displayName"
                    type="text"
                    maxLength={50}
                    placeholder="e.g. Custom Manager"
                    className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content placeholder:text-base-content/20 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all font-bold ${fieldErrors.displayName
                      ? 'border-error'
                      : 'border-base-300'
                      }`}
                    value={form.displayName}
                    onBlur={(e) =>
                      handleBlur('displayName', e.target.value)
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm(f => ({ ...f, displayName: val }));

                      if (fieldErrors.displayName) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          displayName: null
                        }));
                      }
                    }}
                  />

                  {fieldErrors.displayName && (
                    <p className="text-error text-xs mt-1 font-medium">
                      {fieldErrors.displayName}
                    </p>
                  )}
                </div>

                {/* Scope */}
                <div className="space-y-2">
                  <label htmlFor="scope" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Scope
                  </label>

                  <div className="relative" ref={scopeDropdownRef}>
                    <button
                      id="scope"
                      type="button"
                      onClick={() =>
                        setIsScopeOpen(!isScopeOpen)
                      }
                      className="w-full px-4 py-3 bg-base-100 border border-base-300 rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all cursor-pointer group flex items-center justify-between"
                    >
                      <span className="truncate flex-1 text-left">
                        {form.scope === 'platform'
                          ? 'Platform Wide'
                          : form.scope === 'account'
                            ? 'Account Level'
                            : 'Sub-Account Level'}
                      </span>

                      <ChevronDown
                        size={14}
                        className={`text-base-content/20 group-hover:text-primary transition-transform ${isScopeOpen ? 'rotate-180' : ''
                          }`}
                      />
                    </button>

                    {isScopeOpen && (
                      <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col">
                        {[
                          {
                            val: 'platform',
                            label: 'Platform Wide'
                          },
                          {
                            val: 'account',
                            label: 'Account Level'
                          },
                          {
                            val: 'sub_account',
                            label: 'Sub-Account Level'
                          }
                        ].filter(opt => availableScopes.includes(opt.val)).map((opt) => (
                          <button
                            key={opt.val}
                            type="button"
                            onClick={() => {
                              setForm((f) => ({
                                ...f,
                                scope: opt.val,
                                permissionIds: []
                              }));

                              setIsScopeOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${form.scope === opt.val
                              ? 'bg-primary/5'
                              : ''
                              }`}
                          >
                            <span
                              className={`text-[11px] font-black uppercase tracking-widest ${form.scope === opt.val
                                ? 'text-primary'
                                : 'text-base-content/40 group-hover:text-primary'
                                }`}
                            >
                              {opt.label}
                            </span>

                            {form.scope === opt.val && (
                              <Check
                                size={12}
                                className="text-primary ml-auto"
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* Permissions */}
            <PermissionGrid
              isLoading={loading}
              permissions={filteredPermissions}
              selectedIds={form.permissionIds}
              onChange={(newIds) =>
                setForm((f) => ({
                  ...f,
                  permissionIds: newIds
                }))
              }
            />
            {fieldErrors.permissionIds && (
              <p className="text-error text-xs mt-2 font-medium">
                {fieldErrors.permissionIds}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-12 border-t border-base-content/5">

              <button
                type="button"
                onClick={() => navigate('/roles')}
                className="flex-1 py-4 border border-base-content/20 text-base-content/70 bg-base-100 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-base-200 transition"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-premium"
              >
                {submitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span>Committing...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <PlusCircle size={18} />
                    <span>Create Role</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewRole;